/**
 * AlertNotificationsProvider — bell + toast + dropdown notifications.
 *
 * Subscribes to the existing `['alerts']` TanStack Query cache (which is
 * already kept live by the `useAlerts` poll) and exposes:
 *
 * - `recent`: last N alerts for the active site (default 20), sorted
 *   by timestamp. Read state is folded in so the consumer can render
 *   a different visual.
 * - `unreadCount`: number of recent alerts that haven't been read.
 * - `toasts`: critical/warning toasts currently on screen (max 3, 5s TTL).
 *   The whole stack holds still while any toast in it is hovered, focused
 *   or touched: nothing expires and a new toast waits (`toastsWaiting`),
 *   so nothing slides under a finger (`toast-stack.ts`).
 * - `markAllRead()`, `markRead(id)`, `markUnread(id)`, `markAllUnread()`,
 *   `dismissToast(id)`, `holdToast(id, why)` / `releaseToast(id, why)`
 * - `showWhere(focus)` / `showAlertWhere(alert)`: the Overview, pointed at
 *   a place (twin slice 1, plan W4 step 13). A site audience's toast and
 *   bell clicks go there too, never to `/devices`.
 *
 * Read state persists in `localStorage` keyed by user+site so a
 * browser restart doesn't re-show every alert as unread.
 *
 * There is deliberately NO archive here any more. The old
 * localStorage-only archive hid alerts in this one browser while the
 * same rows stayed open on the Alerts tab, in the badge, and for every
 * other operator — two contradictory views of one server row, with no
 * server call anywhere in the path. Server-side resolve and per-device
 * mutes (Alerts screen) are how an alert leaves the bell now, for
 * everyone at once. See `persistence.ts` for how previously-persisted
 * archive payloads are tolerated and scrubbed.
 *
 * The provider should be mounted inside `SiteProvider` (it calls
 * `useActiveSite` to scope alerts to the active site) but outside the
 * `<Outlet>` so every screen sees the same notifications.
 */
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { useSafeSiteId } from '../useActiveSite';
import { alertsKey } from '../../api/scope';
import { adaptAlert, type AlertViewModel } from '../../api/adapters';
import {
  MAX_RECENT_ALERTS,
  MAX_TOASTS,
  TOAST_TTL_MS,
  type ToastHold,
  type ToastItem,
} from './constants';
import {
  AlertNotificationsContext,
  AlertNotificationsLiveContext,
  AlertNotificationsStableContext,
  type AlertNotificationsContextValue,
  type AlertNotificationsLiveSlice,
  type AlertNotificationsStableSlice,
  type BellAlertItem,
} from './context';
import {
  STORAGE_VERSION,
  _getStorageState,
  loadPersistedState,
  probeStorageAvailable,
  saveReadKey,
  stateKey,
  type PersistedState,
} from './persistence';
import { playAlertChime, isMuted, setMuted, subscribeMute } from '../alert-audio';
import { useBrowserAlertNotifications } from '../browser-notifications';
import {
  ALERT_DEEP_LINK_EVENT,
  navigationTargetFor,
  type AlertNavigationTarget,
} from '../alert-deep-link';
import { useUserPreferences } from '../useUserPreferences';
import { isBellVisible, isToastWorthy, popsUp } from '../alert-filter';
import { withAlertWords } from './alert-words';
import { useAlertWords } from './useAlertWords';
import { monotonicNow } from '../clock';
import type { LocateFocus, LocateFrom } from '../locate';
import {
  ALERT_SHOW_WHERE_VIEW,
  alertShowWhere,
  newLandingState,
  showWhereHref,
} from '../show-where';
import {
  holdDeepLink,
  pathnameOf,
  pendingDecision,
  type PendingDeepLink,
} from './pending-deep-link';
import {
  admitToasts,
  EMPTY_TOAST_STACK,
  holdToastIn,
  releaseToastIn,
  removeToast,
  settleToastStack,
  sweepToastStack,
  type ToastStackState,
} from './toast-stack';

export { MAX_RECENT_ALERTS, MAX_TOASTS, TOAST_TTL_MS, type ToastItem };
export type { BellAlertItem };

/** What a toast is deduplicated on: the alert AND its severity, so a row
 *  raised in place (a low battery gone very low) pops up again. */
function toastKey(alert: AlertViewModel): string {
  return `${alert.id}#${alert.severity}`;
}

export function AlertNotificationsProvider({ children }: { children: ReactNode }) {
  const { user, roles } = useAuth();
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // `audienceFor`, never a role list of our own: a mixed account reads
  // ops, and `service_operator` alone reads site (amendment A2).
  const audience = audienceFor(roles);
  const { prefs } = useUserPreferences();
  // The Profile's "Pop up for low battery" (`popsUp`). A PRIMITIVE, read
  // through a ref by the subscription below and never one of its
  // dependencies. Until spa 0.5.69 that effect depended on `prefs.battery`,
  // an object `mergePreferences` rebuilds on EVERY preference save -- the
  // Help toggle, a column layout, the theme -- and each re-run cleared the
  // toasts already seen, so every open warning and critical toasted and
  // chimed again, dismissed ones included.
  const lowPopups = prefs.battery.lowPopups;
  const lowPopupsRef = useRef(lowPopups);
  useEffect(() => {
    lowPopupsRef.current = lowPopups;
  }, [lowPopups]);

  const userId = user?.id ?? 'anonymous';
  const storageKey = stateKey(userId, siteId);
  const persistedRef = useRef<PersistedState>(loadPersistedState(storageKey));

  // Mirror `recent` and `readSet` into refs so callbacks and effects
  // can read the latest values without taking them as dependencies
  // (which would invalidate memoization on every alerts-cache tick).
  const recentRef = useRef<AlertViewModel[]>([]);
  const readSetRef = useRef<Set<string>>(new Set(persistedRef.current.read));

  const [readSet, setReadSet] = useState<Set<string>>(() => new Set(persistedRef.current.read));
  const [recent, setRecent] = useState<AlertViewModel[]>([]);
  // Every bell-visible row, not only the newest MAX_RECENT_ALERTS the bell
  // lists: what the desktop notifications are fed (review V4-1).
  const [visibleAll, setVisibleAll] = useState<AlertViewModel[]>([]);
  const [toastStack, setToastStack] = useState<ToastStackState>(EMPTY_TOAST_STACK);
  const toasts = toastStack.shown as ToastItem[];
  const toastsWaiting = toastStack.waiting.length;
  /** A change to the stack that may let a held one go (`toast-stack.ts`). */
  const changeToastStack = useCallback(
    (change: (prev: ToastStackState) => ToastStackState) =>
      setToastStack((prev) =>
        settleToastStack(prev, change(prev), Date.now(), TOAST_TTL_MS, MAX_TOASTS),
      ),
    [],
  );
  const [persistenceAvailable, setPersistenceAvailable] = useState<boolean>(() =>
    probeStorageAvailable(),
  );
  // Which alerts have had their toast, as `toastKey`s: an alert raised in
  // place to a new severity is news again.
  const seenToastIdsRef = useRef<Set<string>>(new Set());
  // Which delivery of the alerts list `recent` holds: null until this
  // subscription has had one, then a key per subscription. The OS
  // notification hook records the first rows under each key as the
  // backlog (already open, so never "Critical alert" news): a page load,
  // and every open tab reloads on a deploy, must not raise the site's
  // whole open list.
  const [deliveryKey, setDeliveryKey] = useState<string | null>(null);
  const subscriptionSeqRef = useRef(0);

  // Subscribe to the alerts cache (already kept live by useAlerts' poll).
  useEffect(() => {
    persistedRef.current = loadPersistedState(storageKey);
    setReadSet(new Set(persistedRef.current.read));
    // Reset local state on site/user switch so we don't show stale toasts
    // or cross-site alerts.
    setRecent([]);
    setVisibleAll([]);
    setToastStack(EMPTY_TOAST_STACK);
    setDeliveryKey(null);
    seenToastIdsRef.current = new Set();
    subscriptionSeqRef.current += 1;
    const delivery = `${storageKey}#${subscriptionSeqRef.current}`;
    // The first list this subscription is handed is the BACKLOG, as it is
    // for the OS notifications (`browser-notifications.ts`): whatever was
    // already open, however long ago -- a page load, a site switch, and
    // every open tab reloading after a deploy. It is recorded as seen and
    // raises no toast and no chime, so a row that has been open for an hour
    // does not pop up again on each of them (spa 0.5.69). Its rows are in
    // the bell. The cost is the OS notification's: an alert that opened in
    // the instant before a load gets no toast.
    let backlogTaken = false;

    function update(alerts: unknown) {
      if (!Array.isArray(alerts)) {
        setRecent([]);
        setVisibleAll([]);
        return;
      }
      const viewModels: AlertViewModel[] = alerts
        .filter((a): a is unknown => a !== null && typeof a === 'object')
        .map((a) => adaptAlert(a));
      viewModels.sort((a, b) => (a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0));
      // Toasts operate on the full list so we can keep an internal
      // reference for click-through even when the alert is hidden
      // from the dropdown -- and because a row RAISED IN PLACE keeps its
      // opening time: a low battery gone very low behind twenty newer
      // alerts is not among the twenty the bell lists, and must still pop
      // up (review V4-1). The desktop notifications read the full list too.
      const trimmed = viewModels.slice(0, MAX_RECENT_ALERTS);
      // No per-browser filter on the bell: a server row is listed here
      // whatever this person's pop-up choices are.
      const visible = trimmed.filter(isBellVisible);
      setRecent(visible);
      setVisibleAll(viewModels.filter(isBellVisible));
      // After the rows, never before: the hook takes its backlog from the
      // rows it holds when the key first appears.
      setDeliveryKey(delivery);

      if (!backlogTaken) {
        backlogTaken = true;
        // Every row, not the first MAX_RECENT_ALERTS: one further down that
        // later moves up is no newer for it.
        for (const vm of viewModels) seenToastIdsRef.current.add(toastKey(vm));
        return;
      }
      const nextToasts: ToastItem[] = [];
      for (const vm of viewModels) {
        if (!isToastWorthy(vm)) continue;
        const key = toastKey(vm);
        if (seenToastIdsRef.current.has(key)) continue;
        // Seen even when it does not pop up: ticking the box later does not
        // bring back the low-battery alerts that arrived while it was off.
        seenToastIdsRef.current.add(key);
        if (!popsUp(lowPopupsRef.current, vm)) continue;
        nextToasts.push({ id: vm.id, alert: vm, expiresAt: Date.now() + TOAST_TTL_MS });
      }
      if (nextToasts.length > 0) {
        // Onto the stack, or waiting while a toast in it is held.
        setToastStack((prev) => admitToasts(prev, nextToasts, MAX_TOASTS));
        // Audio chime on new toasts, waiting ones included: the alert
        // arrived. Only play when not muted.
        if (!isMuted()) {
          playAlertChime();
        }
      }
    }

    const cached = qc.getQueryData<unknown[]>(alertsKey(siteId));
    if (cached) update(cached);

    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const key = event.query.queryKey;
      if (key[0] !== 'alerts' || key[1] !== siteId) return;
      const data = event.query.state.data as unknown;
      update(data);
    });

    return () => {
      unsubscribe();
    };
    // NOT the preferences: see `lowPopupsRef`.
  }, [qc, siteId, storageKey]);

  // Persist read state. Also what rewrites a pre-v3 payload without
  // its retired `archived` half the first time anything changes.
  useEffect(() => {
    saveReadKey(storageKey, STORAGE_VERSION, Array.from(readSet));
  }, [storageKey, readSet]);

  // Surface persistent localStorage failures (Safari Private,
  // sandboxed iframes). The save helper flips the module-level
  // `storageWriteFailed` latch after a throw; mirror it into React
  // state so the context value can advertise it to consumers.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (_getStorageState().failed && persistenceAvailable) {
        setPersistenceAvailable(false);
      }
    }, 5_000);
    return () => window.clearInterval(id);
  }, [persistenceAvailable]);

  useEffect(() => {
    if (toasts.length === 0) return;
    const handle = window.setInterval(() => {
      // Nothing expires while the stack is held (`toast-stack.ts`).
      setToastStack((prev) => sweepToastStack(prev, Date.now()));
    }, 500);
    return () => window.clearInterval(handle);
  }, [toasts.length]);

  const markRead = useCallback((id: string) => {
    if (readSetRef.current.has(id)) return;
    const next = new Set(readSetRef.current);
    next.add(id);
    setReadSet(next);
  }, []);

  const markUnread = useCallback((id: string) => {
    if (!readSetRef.current.has(id)) return;
    const next = new Set(readSetRef.current);
    next.delete(id);
    setReadSet(next);
  }, []);

  const markAllRead = useCallback(() => {
    const next = new Set(readSetRef.current);
    for (const a of recentRef.current) next.add(a.id);
    setReadSet(next);
  }, []);

  const markAllUnread = useCallback(() => {
    setReadSet(new Set());
  }, []);

  const dismissToast = useCallback(
    (id: string) => changeToastStack((prev) => removeToast(prev, id)),
    [changeToastStack],
  );

  const holdToast = useCallback(
    (id: string, hold: ToastHold) => setToastStack((prev) => holdToastIn(prev, id, hold)),
    [],
  );

  // The last hold on the stack ending lets it go: the waiting toasts come
  // in and every toast gets a fresh TTL, never a shorter one -- a toast
  // read past its expiry must not vanish the instant the pointer leaves it.
  const releaseToast = useCallback(
    (id: string, hold: ToastHold) => changeToastStack((prev) => releaseToastIn(prev, id, hold)),
    [changeToastStack],
  );

  const showWhere = useCallback(
    (focus: LocateFocus, from: LocateFrom | null = null): boolean => {
      // The Building view, as every deep link opens (Decision 7); where it
      // can't be drawn the Overview shows the Map and says why.
      const href = showWhereHref(siteId, ALERT_SHOW_WHERE_VIEW, focus, from);
      if (!href) return false;
      // A PUSH with a fresh landing id (`show-where.ts`): Back returns to
      // where the press was made, and the Overview treats it as a landing
      // even when it is already the mounted screen.
      navigate(href, { state: newLandingState() });
      return true;
    },
    [navigate, siteId],
  );

  const showAlertWhere = useCallback(
    (alert: AlertViewModel) => {
      const where = alertShowWhere(alert);
      if (!where) return;
      // The same "I saw this" gesture as any other click on the row.
      markRead(alert.id);
      changeToastStack((prev) => removeToast(prev, alert.id));
      showWhere(where.focus, where.from);
    },
    [markRead, showWhere, changeToastStack],
  );

  // The ops click's drawer request, held until its screen has mounted
  // (`pending-deep-link.ts`). A ref, not state: holding it must not
  // re-render every consumer, and it is read only in the effect below.
  const pendingDeepLinkRef = useRef<PendingDeepLink | null>(null);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const handleAlertAction = useCallback(
    (alert: AlertViewModel) => {
      // A site audience is pointed at the place, on the Overview -- never
      // at `/devices`, `/fleet` or `/alerts`, and never at a drawer.
      if (audience === 'site') {
        showAlertWhere(alert);
        return;
      }

      // Mark as read on click — this is the user's explicit "I saw
      // this" gesture, so the row tints to the read state immediately.
      markRead(alert.id);

      // Decide where to navigate + which drawer to open.
      const target: AlertNavigationTarget = navigationTargetFor(alert);
      if (target.deepLink) {
        // Fired now for the screen that is already mounted (a click on
        // Fleet about a robot)...
        window.dispatchEvent(
          new CustomEvent(ALERT_DEEP_LINK_EVENT, { detail: target.deepLink }),
        );
        // ...and held for the one that is not yet: routes unmount on
        // navigation, so from any other screen nothing is listening yet.
        pendingDeepLinkRef.current =
          pathnameOf(target.path) === pathnameRef.current
            ? null
            : holdDeepLink(target.deepLink, target.path, monotonicNow());
      }
      // Clear any matching toast so the stack collapses once the
      // operator opens the relevant screen.
      changeToastStack((prev) => removeToast(prev, alert.id));
      navigate(target.path);
    },
    [audience, navigate, markRead, showAlertWhere, changeToastStack],
  );

  // Deliver a held drawer request once the location has committed to its
  // screen. This effect belongs to the provider, which sits above the
  // routed screen, so it runs AFTER that screen's own effects have added
  // its listener.
  useEffect(() => {
    const pending = pendingDeepLinkRef.current;
    if (!pending) return;
    const decision = pendingDecision(pending, location.pathname, monotonicNow());
    if (decision === 'wait') return;
    pendingDeepLinkRef.current = null;
    if (decision === 'deliver') {
      window.dispatchEvent(new CustomEvent(ALERT_DEEP_LINK_EVENT, { detail: pending.link }));
    }
  }, [location.key, location.pathname]);

  // The effect deps must NOT include `recent` or `readSet`: those
  // change on every alerts-cache tick (~10 Hz). We mirror them
  // through refs so long-lived closures read the latest values.
  useEffect(() => {
    recentRef.current = recent;
  }, [recent]);
  useEffect(() => {
    readSetRef.current = readSet;
  }, [readSet]);

  const [muted, setMutedState] = useState<boolean>(() => isMuted());
  const toggleMute = useCallback(() => {
    setMutedState((prev) => {
      const next = !prev;
      setMuted(next);
      return next;
    });
  }, []);

  useEffect(() => subscribeMute(() => setMutedState(isMuted())), []);

  // A hidden tab's OS notification speaks the toast's words: the hotel's
  // for a site audience, so it never carries the code the toast and the
  // bell hide (plan W5 step 12). Ops get the very same rows as before.
  const wordsFor = useAlertWords();
  // Fed from the bell's rows -- all of them, not only the twenty it lists
  // (V4-1) -- so it follows the same pop-up choice as the toasts
  // (`popsUp`): the bell lists every row, the desktop does not.
  const notifiable = useMemo(
    () =>
      visibleAll.filter((a) => popsUp(lowPopups, a)).map((a) => withAlertWords(a, wordsFor(a))),
    [visibleAll, wordsFor, lowPopups],
  );
  // Its click goes where the toast's does for a site audience (the
  // Overview); ops keep `/alerts`, the hook's own default.
  const onNotificationActivate = useMemo(
    () => (audience === 'site' ? showAlertWhere : undefined),
    [audience, showAlertWhere],
  );
  useBrowserAlertNotifications(notifiable, { deliveryKey, onActivate: onNotificationActivate });

  const unreadCount = useMemo(
    () => recent.filter((a) => !readSet.has(a.id)).length,
    [recent, readSet],
  );

  // Derived: read-set baked into recent so consumers don't have to
  // look anything up. `isRead` is precomputed per-render.
  const recentWithState = useMemo<BellAlertItem[]>(
    () => recent.map((a) => ({ ...a, isRead: readSet.has(a.id) })),
    [recent, readSet],
  );

  // Split the context value into two slices so consumers that only
  // read the stable slice (callbacks, muted, readSet) don't
  // re-render on every alerts-cache tick (~10 Hz). The composite
  // `value` is also memoized for backward compatibility with
  // existing `useAlertNotifications()` callers; new callers can
  // opt into the slices via `useAlertNotificationsLive` /
  // `useAlertNotificationsStable` in `useAlertNotifications.ts`.
  const liveSlice = useMemo<AlertNotificationsLiveSlice>(
    () => ({
      recent: recentWithState,
      unreadCount,
      toasts,
      toastsWaiting,
    }),
    [recentWithState, unreadCount, toasts, toastsWaiting],
  );

  const stableSlice = useMemo<AlertNotificationsStableSlice>(
    () => ({
      markAllRead,
      markRead,
      markUnread,
      markAllUnread,
      dismissToast,
      muted,
      toggleMute,
      handleAlertAction,
      showWhere,
      showAlertWhere,
      holdToast,
      releaseToast,
      readSet,
      persistenceAvailable,
    }),
    [
      markAllRead,
      markRead,
      markUnread,
      markAllUnread,
      dismissToast,
      muted,
      toggleMute,
      handleAlertAction,
      showWhere,
      showAlertWhere,
      holdToast,
      releaseToast,
      readSet,
      persistenceAvailable,
    ],
  );

  const value: AlertNotificationsContextValue = useMemo(
    () => ({ ...liveSlice, ...stableSlice }),
    [liveSlice, stableSlice],
  );

  return (
    <AlertNotificationsContext.Provider value={value}>
      <AlertNotificationsLiveContext.Provider value={liveSlice}>
        <AlertNotificationsStableContext.Provider value={stableSlice}>
          {children}
        </AlertNotificationsStableContext.Provider>
      </AlertNotificationsLiveContext.Provider>
    </AlertNotificationsContext.Provider>
  );
}
